import AddTool, { AddToolContext } from './add-tool';
import DoorView from '../../../views/door-view.js';
import SectionView from '../../../views/section-view.js';
import SelectionManager from '../selection-manager';
import logging from '../../../logging';
import { Door, ParametricPart, Section } from '../../../domain';
import { approx, approxGreaterThan, approxOverlaps, round2slot } from '../../../measurement';
import { getParentSectionView } from '../../../controllers/section-controllers';
import { isBoxOnly } from '../../../controllers/drawer-controllers';
import { partElevationAscending } from '../../../controllers/parametric-part-controllers';

const log = logging('tools');


/**
 * Tests if the section can accept a door at the given position.
 */
const canAcceptDoor = (view: SectionView, y: number): boolean => {
    const section = view.model;

    if(section.width < 5) return false;
    if(section.height < 5) return false;
    const
        [bottom, height] = getDoorSpan(section, y),
        top = bottom+height;

    for(const door of section.doors) {
        if(approxOverlaps(door.position.y, door.position.y + door.height,
                          bottom, top))
        {
            return false;
        }
    }
    for(const drawer of section.drawers) {
        if(!isBoxOnly(drawer)) {
            if(approxOverlaps(
                drawer.position.y, drawer.position.y + drawer.height,
                bottom, top))
            {
                return false;
            }
        }
    }

    return true;
}

/**
 * Finds the global elevation of the fixed shelves above and below the given
 * elevation, y.
 */
const getDoorSpan = (model: Section, y: number): [y: number, height: number] => {
    model.parametricParts.sort(partElevationAscending);

    let
        eq: ParametricPart = null,
        below: ParametricPart = null,
        above: ParametricPart = null;
    for(const part of model.parametricParts) {
        if(part.name.startsWith('Fixed')) {
            if(approx(part.position.y, y)) {
                eq = part;
            } else if(approxGreaterThan(y, part.position.y)) {
                below = part;
            } else if(above == null && approxGreaterThan(part.position.y, y)) {
                above = part;
            }
        }
    }

    // This happens when the cursor is over the bottom-most fixed shelf
    if(eq != null && below == null) {
        below = eq;
    }

    return [below.position.y, above.position.y - below.position.y];
};

export class AddDoorTool extends AddTool
{

    /**
     * This is the section that the mouse is currently over.
     */
    target?: SectionView;

    selection: SelectionManager;

    model: Door;
    proxy: DoorView;

    constructor(door: Door, elt: HTMLElement, selection: SelectionManager,
                ctx: AddToolContext)
    {
        super(elt, ctx);

        this.selection = selection;

        const handles = door.hinge == "double" ? [{}, {}] : [{}];

        this.model = Door.fromJSON({
            hinge: door.hinge,
            type: door.type,
            width: 18,
            height: 24,
            open: 0,
            handles,
        });
        this.ctx.defaults.reset(this.model);

        const icon = this.model.clone(true);
        if(icon.hinge.includes('double')) {
            icon.open = 0.125;
            icon.commit(true);
        }

        this.updateThumbnail(icon);
    }

    dragenter(): void {
        log.debug('AddDoorTool.dragenter()');

        this.proxy = new DoorView(this.model);
    }

    dragover(event: PointerEvent, elt: HTMLElement): void {
        log.debug('AddDoorTool.dragover()');

        this.updateWorldPosition(event, elt);
        this.onDisplayChange.trigger();

        const
            y = round2slot(this.worldPosition.y),
            hit = getParentSectionView(this.ctx.picker.topHit);
        if(hit && canAcceptDoor(hit, y))
        {
            const
                proxy = this.proxy,
                model = this.model,
                target: SectionView = this.target = hit,
                [y1, height] = getDoorSpan(target.model, y);

            target.add(proxy);

            model.width = target.model.width + 0.5;
            model.height = height;
            model.position.x = -0.25;
            model.position.y = y1;
            model.position.z = target.model.depth;
        }
        else {
            delete this.target;

            // check if the proxy belongs to the scene ... if not, add it back
            // to the scene.
            if(this.proxy.parent != this.ctx.scene) {
                this.proxy.removeFromParent();
                this.ctx.scene.add(this.proxy);
            }

            // regardless, set the size and position of the model/proxy
            this.model.width = 18;
            this.model.height = 24;
            this.model.position.x = this.worldPosition.x - this.model.width / 2;
            this.model.position.y = this.worldPosition.y - this.model.height / 2;
            this.model.position.z = 25;
        }
    }

    dragleave(): void {
        log.debug('AddDoorTool.dragleave()');
        this.proxy.removeFromParent();
        delete this.target;
        this.onDisplayChange.trigger();
    }

    cancel(): void {
        log.debug('AddDoorTool.cancel()');
        this.proxy.removeFromParent();
        delete this.target;
        this.onDisplayChange.trigger();
    }

    end(): void {
        log.debug('AddDoorTool.end()');
        this.proxy.removeFromParent();
        this.onDisplayChange.trigger();

        // if we ended over a section, then we'll have a defined target
        // property, which means we can drop something.
        if(this.target) {
            this.model.commit();
            const model = this.model.clone(true);
            model.parent = this.target.model;
            this.target.model.doors.push(model);
            this.onSuccess.trigger(model);

            delete this.target;
        }
    }

}
export default AddDoorTool;


